import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './SetupPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/brakepress-api-service';

const SetupPage = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [dbConfig, setDbConfig] = useState({
        type: 'sqlite',
        useDetailed: true,
        url: '',
        engine: 'postgres',
        host: 'localhost',
        port: '5432',
        user: '',
        password: '',
        database: '',
        driver: 'ODBC Driver 18 for SQL Server',
        authentication: 'Default',
        trustServerCertificate: false,
        encrypt: true,
        connectionTimeout: 30
    });
    const [adminData, setAdminData] = useState({
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [storageConfig, setStorageConfig] = useState({
        provider: 'local',
        local_path: '',
        s3_bucket: '',
        s3_access_key: '',
        s3_secret_key: '',
        s3_region: '',
        azure_connection_string: '',
        azure_container: ''
    });
    const [emailConfig, setEmailConfig] = useState({
        mail_server: '',
        mail_port: 587,
        mail_use_tls: true,
        mail_username: '',
        mail_password: '',
        mail_default_sender: ''
    });
    const [ssoConfig, setSsoConfig] = useState({
        azure: { enabled: false, client_id: '', client_secret: '', tenant_id: '' },
        google: { enabled: false, client_id: '', client_secret: '' }
    });
    const navigate = useNavigate();

    useEffect(() => {
        checkStatus();
    }, []);

    const checkStatus = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/setup/status`);
            setStatus(response.data);
            // Pre-fill storage path from server (DATA_PATH env or 'data' fallback)
            if (response.data.default_data_path) {
                setStorageConfig(prev => ({
                    ...prev,
                    local_path: prev.local_path || response.data.default_data_path
                }));
            }
            if (response.data.is_setup) {
                setError("System is already set up. Redirecting to login...");
                setTimeout(() => navigate('/'), 3000);
            }
        } catch (err) {
            console.error("Error checking setup status:", err);
            setError("Failed to connect to the server.");
        }
    };

    const generateDbUrl = () => {
        if (dbConfig.type === 'sqlite') return '';
        if (!dbConfig.useDetailed) return dbConfig.url;
        
        const { engine, host, port, user, password, database, driver, trustServerCertificate, encrypt } = dbConfig;
        const encodedPass = encodeURIComponent(password);
        
        if (engine === 'postgres') {
            return `postgresql://${user}:${encodedPass}@${host}:${port}/${database}`;
        } else if (engine === 'mssql') {
            const params = [];
            if (driver) params.push(`driver=${encodeURIComponent(driver)}`);
            if (trustServerCertificate) params.push('TrustServerCertificate=yes');
            if (encrypt) params.push('Encrypt=yes');
            
            let urlUserPass = `${user}:${encodedPass}@`;
            
            if (dbConfig.authentication === 'WindowsAuthentication') {
                params.push('Trusted_Connection=yes');
                urlUserPass = '';
            } else if (dbConfig.authentication !== 'Default') {
                params.push(`Authentication=${dbConfig.authentication}`);
            }
            
            if (dbConfig.connectionTimeout) params.push(`Connection+Timeout=${dbConfig.connectionTimeout}`);

            const portPart = port ? `:${port}` : ':1433';
            const query = params.length > 0 ? `?${params.join('&')}` : '';
            return `mssql+pyodbc://${urlUserPass}${host}${portPart}/${database}${query}`;
        } else if (engine === 'mysql') {
            return `mysql+pymysql://${user}:${encodedPass}@${host}:${port}/${database}`;
        }
        return dbConfig.url;
    };

    const handleDbConfig = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        const dbUrl = generateDbUrl();
        
        if (dbConfig.type === 'external' && !dbUrl) {
            setError("Database configuration is incomplete.");
            setLoading(false);
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/api/setup/config-db`, {
                db_type: dbConfig.type,
                db_url: dbUrl
            });
            // Automatically move to Storage Configuration
            setStep(3);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to configure database.");
        } finally {
            setLoading(false);
        }
    };

    const handleStorageConfig = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await axios.post(`${API_BASE_URL}/api/setup/storage`, storageConfig);
            setStep(4);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to configure storage.");
        } finally {
            setLoading(false);
        }
    };

    const handleEmailConfig = async (e, skip = false) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            await axios.post(`${API_BASE_URL}/api/setup/email`, { ...emailConfig, skip });
            setStep(5);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to configure email.");
        } finally {
            setLoading(false);
        }
    };

    const handleTestEmail = async () => {
        setTestLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/setup/test-email`, emailConfig);
            setSuccessMessage(response.data.message);
        } catch (err) {
            setError(err.response?.data?.message || "Connection test failed.");
        } finally {
            setTestLoading(false);
        }
    };

    const handleAdminSubmit = async (e) => {
        e.preventDefault();
        if (adminData.password !== adminData.confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await axios.post(`${API_BASE_URL}/api/setup/admin`, {
                email: adminData.email,
                password: adminData.password
            });
            setStep(6);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to create admin user.");
        } finally {
            setLoading(false);
        }
    };

    const handleSSOConfig = async (e, skip = false) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (!skip) {
                const providers = [
                    { name: 'azure', ...ssoConfig.azure },
                    { name: 'google', ...ssoConfig.google }
                ];
                await axios.post(`${API_BASE_URL}/api/setup/sso`, { providers });
            }
            setStep(7);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to configure SSO.");
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="setup-step animated fadeIn">
                        <h2>Welcome to BrakePress</h2>
                        <p>Thank you for choosing BrakePress Cloud. This wizard will guide you through the initial configuration of your environment, including database setup and administrator creation.</p>
                        <div className="feature-list">
                            <div className="feature-item">
                                <span className="feature-icon">📁</span>
                                <div className="feature-text">
                                    <strong>Flexible Storage</strong>
                                    <span>Choose between local SQLite or external database clusters.</span>
                                </div>
                            </div>
                            <div className="feature-item">
                                <span className="feature-icon">🛡️</span>
                                <div className="feature-text">
                                    <strong>Secure Setup</strong>
                                    <span>Define your own master credentials for the platform.</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            className="setup-btn primary" 
                            onClick={() => setStep(2)}
                        >
                            Get Started
                        </button>
                    </div>
                );
            case 2:
                return (
                    <div className="setup-step animated fadeIn">
                        <h2>Database Configuration</h2>
                        <p>Choose where you want to store your application data.</p>
                        <form onSubmit={handleDbConfig}>
                            <div className="db-options">
                                <label className={`db-option ${dbConfig.type === 'sqlite' ? 'selected' : ''}`}>
                                    <input 
                                        type="radio" 
                                        name="dbType" 
                                        value="sqlite" 
                                        checked={dbConfig.type === 'sqlite'}
                                        onChange={(e) => setDbConfig({...dbConfig, type: e.target.value})}
                                    />
                                    <div className="option-content">
                                        <strong>Local SQLite</strong>
                                        <span>Recommended for small deployments or testing. Stores data in a local file.</span>
                                    </div>
                                </label>
                                <label className={`db-option ${dbConfig.type === 'external' ? 'selected' : ''}`}>
                                    <input 
                                        type="radio" 
                                        name="dbType" 
                                        value="external" 
                                        checked={dbConfig.type === 'external'}
                                        onChange={(e) => setDbConfig({...dbConfig, type: e.target.value})}
                                    />
                                    <div className="option-content">
                                        <strong>External Database</strong>
                                        <span>Recommended for production. Connect to PostgreSQL, SQL Server, etc.</span>
                                    </div>
                                </label>
                            </div>

                            {dbConfig.type === 'external' && (
                                <div className="external-db-config animated fadeIn">
                                    <div className="config-mode-toggle">
                                        <button 
                                            type="button"
                                            className={`toggle-btn ${dbConfig.useDetailed ? 'active' : ''}`}
                                            onClick={() => setDbConfig({...dbConfig, useDetailed: true})}
                                        >
                                            Detailed
                                        </button>
                                        <button 
                                            type="button"
                                            className={`toggle-btn ${!dbConfig.useDetailed ? 'active' : ''}`}
                                            onClick={() => setDbConfig({...dbConfig, useDetailed: false})}
                                        >
                                            Connection URL
                                        </button>
                                    </div>

                                    {!dbConfig.useDetailed ? (
                                        <div className="form-group animated fadeIn">
                                            <label>Database Connection URL</label>
                                            <input 
                                                type="text" 
                                                required 
                                                value={dbConfig.url}
                                                onChange={(e) => setDbConfig({...dbConfig, url: e.target.value})}
                                                placeholder="e.g. postgresql://user:pass@localhost:5432/dbname"
                                            />
                                            <small>Full SQLAlchemy connection string.</small>
                                        </div>
                                    ) : (
                                        <div className="detailed-config animated fadeIn">
                                            <div className="form-group">
                                                <label>Database Engine</label>
                                                <select 
                                                    className="setup-select"
                                                    value={dbConfig.engine}
                                                    onChange={(e) => {
                                                        const engine = e.target.value;
                                                        let port = '5432';
                                                        if (engine === 'mssql') port = '1433';
                                                        if (engine === 'mysql') port = '3306';
                                                        setDbConfig({...dbConfig, engine, port});
                                                    }}
                                                >
                                                    <option value="postgres">PostgreSQL</option>
                                                    <option value="mssql">MS SQL Server</option>
                                                    <option value="mysql">MySQL / MariaDB</option>
                                                </select>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group flex-2">
                                                    <label>Host / Server</label>
                                                    <input type="text" required value={dbConfig.host} onChange={(e) => setDbConfig({...dbConfig, host: e.target.value})} placeholder="localhost" />
                                                </div>
                                                <div className="form-group flex-1">
                                                    <label>Port</label>
                                                    <input type="text" required value={dbConfig.port} onChange={(e) => setDbConfig({...dbConfig, port: e.target.value})} placeholder={dbConfig.engine === 'mssql' ? '1433' : '5432'} />
                                                </div>
                                            </div>

                                            {dbConfig.engine === 'mssql' && (
                                                <div className="form-row animated fadeIn">
                                                    <div className="form-group flex-2">
                                                        <label>ODBC Driver</label>
                                                        <input type="text" value={dbConfig.driver} onChange={(e) => setDbConfig({...dbConfig, driver: e.target.value})} placeholder="ODBC Driver 18 for SQL Server" />
                                                    </div>
                                                    <div className="form-group flex-1">
                                                        <label>Auth Mode</label>
                                                        <select 
                                                            className="setup-select"
                                                            value={dbConfig.authentication}
                                                            onChange={(e) => setDbConfig({...dbConfig, authentication: e.target.value})}
                                                        >
                                                            <option value="Default">Default (SQL Auth)</option>
                                                            <option value="WindowsAuthentication">Windows Authentication (AD Domain)</option>
                                                            <option value="ActiveDirectoryPassword">Azure AD Password</option>
                                                            <option value="ActiveDirectoryServicePrincipal">Azure Service Principal</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            )}

                                            {dbConfig.authentication !== 'WindowsAuthentication' && (
                                                <div className="form-row animated fadeIn">
                                                    <div className="form-group">
                                                        <label>Username</label>
                                                        <input type="text" required value={dbConfig.user} onChange={(e) => setDbConfig({...dbConfig, user: e.target.value})} placeholder="postgres" />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Password</label>
                                                        <input type="password" required value={dbConfig.password} onChange={(e) => setDbConfig({...dbConfig, password: e.target.value})} placeholder="••••••••" />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="form-group">
                                                <label>Database Name</label>
                                                <input type="text" required value={dbConfig.database} onChange={(e) => setDbConfig({...dbConfig, database: e.target.value})} placeholder="brakepress_db" />
                                            </div>

                                            {dbConfig.engine === 'mssql' && (
                                                <div className="mssql-extras animated fadeIn">
                                                    {(dbConfig.authentication === 'ActiveDirectoryPassword' || dbConfig.authentication === 'ActiveDirectoryServicePrincipal') && (
                                                        <div className="azure-firewall-hint">
                                                            <span>🔥</span>
                                                            <div>
                                                                <strong>Azure SQL Firewall</strong>
                                                                <span>Ensure this server's IP is whitelisted under <em>Azure Portal → SQL Server → Networking → Firewall rules</em>.</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {dbConfig.authentication === 'WindowsAuthentication' && (
                                                        <div className="info-banner animated fadeIn" style={{marginBottom: '15px'}}>
                                                            <span className="info-icon">ℹ️</span>
                                                            <p style={{margin: 0, fontSize: '13px'}}>
                                                                For Windows Authentication, the Brakepress API Service must be configured in <strong>services.msc</strong> to 'Log On' as the Domain User.
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div className="form-row">
                                                        <div className="form-group flex-1">
                                                            <label>Connection Timeout (s)</label>
                                                            <input type="number" value={dbConfig.connectionTimeout} onChange={(e) => setDbConfig({...dbConfig, connectionTimeout: e.target.value})} placeholder="30" />
                                                        </div>
                                                    </div>
                                                    <div className="form-row-checkbox">
                                                        <label className="checkbox-label">
                                                            <input type="checkbox" checked={dbConfig.trustServerCertificate} onChange={(e) => setDbConfig({...dbConfig, trustServerCertificate: e.target.checked})} />
                                                            Trust Server Certificate
                                                        </label>
                                                        <label className="checkbox-label">
                                                            <input type="checkbox" checked={dbConfig.encrypt} onChange={(e) => setDbConfig({...dbConfig, encrypt: e.target.checked})} />
                                                            Encrypt Connection
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <small className="driver-hint">Ensure the required database driver is installed on the server.</small>
                                </div>
                            )}

                            <div className="button-group">
                                <button
                                    type="button"
                                    className="setup-btn back"
                                    onClick={() => setStep(1)}
                                    disabled={loading}
                                >
                                    ← Back
                                </button>
                                <button 
                                    type="submit" 
                                    className="setup-btn primary" 
                                    disabled={loading}
                                >
                                    {loading ? 'Connecting...' : 'Connect & Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                );
            case 3:
                return (
                    <div className="setup-step animated fadeIn">
                        <h2>Storage Configuration</h2>
                        <p>Configure where the application will store media files and reports.</p>
                        <form onSubmit={handleStorageConfig}>
                            <div className="form-group">
                                <label>Storage Provider</label>
                                <select 
                                    value={storageConfig.provider} 
                                    onChange={(e) => setStorageConfig({...storageConfig, provider: e.target.value})}
                                    className="setup-select"
                                >
                                    <option value="local">Local File System (Edge/Standalone)</option>
                                    <option value="s3">AWS S3 Bucket (Cloud)</option>
                                    <option value="azure">Azure Blob Storage (Cloud)</option>
                                </select>
                            </div>

                            {storageConfig.provider === 'local' && (
                                <div className="form-group animated fadeIn">
                                    <label>Storage Folder Path</label>
                                    <input 
                                        type="text" 
                                        required 
                                        value={storageConfig.local_path}
                                        onChange={(e) => setStorageConfig({...storageConfig, local_path: e.target.value})}
                                        placeholder="e.g. data"
                                    />
                                    <small>Relative to application root or absolute path.</small>
                                </div>
                            )}

                            {storageConfig.provider === 's3' && (
                                <div className="animated fadeIn">
                                    <div className="form-group">
                                        <label>S3 Bucket Name</label>
                                        <input type="text" required value={storageConfig.s3_bucket} onChange={(e) => setStorageConfig({...storageConfig, s3_bucket: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Access Key</label>
                                        <input type="text" required value={storageConfig.s3_access_key} onChange={(e) => setStorageConfig({...storageConfig, s3_access_key: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Secret Key</label>
                                        <input type="password" required value={storageConfig.s3_secret_key} onChange={(e) => setStorageConfig({...storageConfig, s3_secret_key: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Region</label>
                                        <input type="text" required value={storageConfig.s3_region} onChange={(e) => setStorageConfig({...storageConfig, s3_region: e.target.value})} placeholder="e.g. us-east-1" />
                                    </div>
                                </div>
                            )}

                            {storageConfig.provider === 'azure' && (
                                <div className="animated fadeIn">
                                    <div className="form-group">
                                        <label>Connection String</label>
                                        <input type="text" required value={storageConfig.azure_connection_string} onChange={(e) => setStorageConfig({...storageConfig, azure_connection_string: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Container Name</label>
                                        <input type="text" required value={storageConfig.azure_container} onChange={(e) => setStorageConfig({...storageConfig, azure_container: e.target.value})} />
                                    </div>
                                </div>
                            )}

                            <div className="button-group">
                                <button
                                    type="button"
                                    className="setup-btn back"
                                    onClick={() => setStep(2)}
                                    disabled={loading}
                                >
                                    ← Back
                                </button>
                                <button type="submit" className="setup-btn primary" disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Storage Config'}
                                </button>
                            </div>
                        </form>
                    </div>
                );
            case 4:
                return (
                    <div className="setup-step animated fadeIn">
                        <h2>Email Configuration</h2>
                        <div className="info-banner">
                            <span className="info-icon">ℹ️</span>
                            <p>Email configuration is used for password recovery, system alerts, and automated reporting. You can skip this now and configure it later in Settings.</p>
                        </div>
                        <form onSubmit={(e) => handleEmailConfig(e)}>
                            <div className="form-group">
                                <label>SMTP Server</label>
                                <input type="text" value={emailConfig.mail_server} onChange={(e) => setEmailConfig({...emailConfig, mail_server: e.target.value})} placeholder="e.g. smtp.gmail.com" />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Port</label>
                                    <input type="number" value={emailConfig.mail_port} onChange={(e) => setEmailConfig({...emailConfig, mail_port: e.target.value})} />
                                </div>
                                <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '25px' }}>
                                    <label style={{ margin: 0, marginRight: '10px' }}>Use TLS</label>
                                    <input type="checkbox" checked={emailConfig.mail_use_tls} onChange={(e) => setEmailConfig({...emailConfig, mail_use_tls: e.target.checked})} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>SMTP Username</label>
                                <input 
                                    type="text" 
                                    value={emailConfig.mail_username} 
                                    onChange={(e) => setEmailConfig({...emailConfig, mail_username: e.target.value})}
                                    onBlur={(e) => {
                                        // Pre-fill default sender with username if it's still empty
                                        if (!emailConfig.mail_default_sender) {
                                            setEmailConfig(prev => ({...prev, mail_default_sender: e.target.value}));
                                        }
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label>SMTP Password</label>
                                <input type="password" value={emailConfig.mail_password} onChange={(e) => setEmailConfig({...emailConfig, mail_password: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label>Default Sender Address</label>
                                <input 
                                    type="text" 
                                    value={emailConfig.mail_default_sender}
                                    onChange={(e) => setEmailConfig({...emailConfig, mail_default_sender: e.target.value})}
                                    placeholder='e.g. "BrakePress" <noreply@example.com>'
                                />
                                <small>The "From" address shown on outgoing emails. Defaults to username if left blank.</small>
                            </div>
                            <div className="button-group">
                                <button 
                                    type="button" 
                                    className="setup-btn tertiary" 
                                    onClick={handleTestEmail} 
                                    disabled={testLoading || loading}
                                >
                                    {testLoading ? 'Testing...' : 'Test Connection'}
                                </button>
                                <div className="button-row">
                                    <button
                                        type="button"
                                        className="setup-btn back"
                                        onClick={() => setStep(3)}
                                        disabled={loading || testLoading}
                                    >
                                        ← Back
                                    </button>
                                    <button type="submit" className="setup-btn primary" disabled={loading || testLoading}>
                                        {loading ? 'Saving...' : 'Save Email Config'}
                                    </button>
                                </div>
                                <button type="button" className="setup-btn secondary" onClick={() => handleEmailConfig(null, true)} disabled={loading}>
                                    Skip for Now
                                </button>
                            </div>
                        </form>
                    </div>
                );
            case 5:
                return (
                    <div className="setup-step animated fadeIn">
                        <h2>Create SuperAdmin Account</h2>
                        <p>This account will have full access to manage the system, users, and configurations.</p>
                        <form onSubmit={handleAdminSubmit}>
                            <div className="form-group">
                                <label>Admin Email</label>
                                <input 
                                    type="email" 
                                    required 
                                    value={adminData.email}
                                    onChange={(e) => setAdminData({...adminData, email: e.target.value})}
                                    placeholder="admin@example.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <input 
                                    type="password" 
                                    required 
                                    value={adminData.password}
                                    onChange={(e) => setAdminData({...adminData, password: e.target.value})}
                                    placeholder="Minimum 8 characters"
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm Password</label>
                                <input 
                                    type="password" 
                                    required 
                                    value={adminData.confirmPassword}
                                    onChange={(e) => setAdminData({...adminData, confirmPassword: e.target.value})}
                                    placeholder="Re-enter password"
                                />
                            </div>
                            <div className="button-group">
                                <button
                                    type="button"
                                    className="setup-btn back"
                                    onClick={() => setStep(4)}
                                    disabled={loading}
                                >
                                    ← Back
                                </button>
                                <button 
                                    type="submit" 
                                    className="setup-btn primary" 
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Create Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                );
            case 6:
                return (
                    <div className="setup-step animated fadeIn">
                        <h2>Configure Single Sign-On (Optional)</h2>
                        <p>Enable your team to log in using their corporate identities.</p>
                        
                        <div className="sso-setup-options">
                            {/* Azure AD Section */}
                            <div className="sso-provider-card">
                                <div className="provider-header">
                                    <img src="https://authjs.dev/img/providers/azure.svg" alt="Azure" />
                                    <h3>Microsoft Azure AD</h3>
                                    <input 
                                        type="checkbox" 
                                        checked={ssoConfig.azure.enabled} 
                                        onChange={(e) => setSsoConfig({...ssoConfig, azure: {...ssoConfig.azure, enabled: e.target.checked}})}
                                    />
                                </div>
                                {ssoConfig.azure.enabled && (
                                    <div className="provider-fields animated slideInDown">
                                        <input type="text" placeholder="Tenant ID" value={ssoConfig.azure.tenant_id} onChange={(e) => setSsoConfig({...ssoConfig, azure: {...ssoConfig.azure, tenant_id: e.target.value}})} />
                                        <input type="text" placeholder="Client ID" value={ssoConfig.azure.client_id} onChange={(e) => setSsoConfig({...ssoConfig, azure: {...ssoConfig.azure, client_id: e.target.value}})} />
                                        <input type="password" placeholder="Client Secret" value={ssoConfig.azure.client_secret} onChange={(e) => setSsoConfig({...ssoConfig, azure: {...ssoConfig.azure, client_secret: e.target.value}})} />
                                    </div>
                                )}
                            </div>

                            {/* Google Section */}
                            <div className="sso-provider-card">
                                <div className="provider-header">
                                    <img src="https://authjs.dev/img/providers/google.svg" alt="Google" />
                                    <h3>Google Workspace</h3>
                                    <input 
                                        type="checkbox" 
                                        checked={ssoConfig.google.enabled} 
                                        onChange={(e) => setSsoConfig({...ssoConfig, google: {...ssoConfig.google, enabled: e.target.checked}})}
                                    />
                                </div>
                                {ssoConfig.google.enabled && (
                                    <div className="provider-fields animated slideInDown">
                                        <input type="text" placeholder="Client ID" value={ssoConfig.google.client_id} onChange={(e) => setSsoConfig({...ssoConfig, google: {...ssoConfig.google, client_id: e.target.value}})} />
                                        <input type="password" placeholder="Client Secret" value={ssoConfig.google.client_secret} onChange={(e) => setSsoConfig({...ssoConfig, google: {...ssoConfig.google, client_secret: e.target.value}})} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="button-group">
                            <div className="button-row">
                                <button
                                    type="button"
                                    className="setup-btn back"
                                    onClick={() => setStep(5)}
                                    disabled={loading}
                                >
                                    ← Back
                                </button>
                                <button className="setup-btn primary" onClick={handleSSOConfig} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save & Continue'}
                                </button>
                            </div>
                            <button className="setup-btn secondary" onClick={() => handleSSOConfig(null, true)} disabled={loading}>
                                Skip SSO Setup
                            </button>
                        </div>
                    </div>
                );
            case 7:
                return (
                    <div className="setup-step animated fadeIn">
                        <div className="success-icon">✓</div>
                        <h2>Setup Complete!</h2>
                        <p>Your system has been successfully initialized and your admin account is ready.</p>
                        <button 
                            className="setup-btn primary" 
                            onClick={() => navigate('/')}
                        >
                            Go to Login
                        </button>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="setup-container">
            <div className="setup-card">
                <div className="setup-header">
                    <img src="/logo192.png" alt="Logo" className="setup-logo" />
                    <h1>BrakePress Cloud</h1>
                </div>
                
                <div className="setup-progress">
                    <div className={`progress-dot ${step >= 1 ? 'active' : ''}`}>1</div>
                    <div className="progress-line"></div>
                    <div className={`progress-dot ${step >= 2 ? 'active' : ''}`}>2</div>
                    <div className="progress-line"></div>
                    <div className={`progress-dot ${step >= 3 ? 'active' : ''}`}>3</div>
                    <div className="progress-line"></div>
                    <div className={`progress-dot ${step >= 4 ? 'active' : ''}`}>4</div>
                    <div className="progress-line"></div>
                    <div className={`progress-dot ${step >= 5 ? 'active' : ''}`}>5</div>
                    <div className="progress-line"></div>
                    <div className={`progress-dot ${step >= 6 ? 'active' : ''}`}>6</div>
                    <div className="progress-line"></div>
                    <div className={`progress-dot ${step >= 7 ? 'active' : ''}`}>7</div>
                </div>

                {error && <div className="setup-error animated shake">{error}</div>}
                {successMessage && <div className="setup-success animated bounceIn">{successMessage}</div>}
                
                {renderStep()}

                <div className="setup-footer">
                    <p>&copy; 2026 Yottaasys. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default SetupPage;
